import { FC } from "react";
import { formatDurationDe } from "../../../lib/durationDe";
import { durationsFromInstants, type ActualInstants } from "../../../lib/schedule";

interface Props {
  /** The four Ist-Zeitpunkte as the form holds them (`datetime-local` strings, `""` = none). */
  instants: ActualInstants;
  /**
   * Planned Anfahrt/Abfahrt minutes, used where the matching instant is not recorded — the same
   * fallback the server's occupancy COALESCE applies, so the line reads what will be stored.
   */
  fallback?: { travel?: number; departure?: number };
  className?: string;
}

/**
 * The read-only line under the Ist-Zeitpunkte of the office forms: what the server will derive
 * from them. The office types moments, never minutes (Task 2040), so this is the only place the
 * minutes appear on the form — and it recomputes on every keystroke so a slipped date shows up as
 * an absurd duration before the save is refused.
 */
export const ActualTimesSummary: FC<Props> = ({ instants, fallback, className }) => {
  const durations = durationsFromInstants(instants, fallback);

  const text = durations
    ? [
        durations.travel > 0 ? `Anfahrt ${formatDurationDe(durations.travel)}` : null,
        `Arbeit ${formatDurationDe(durations.work)}`,
        durations.departure > 0 ? `Abfahrt ${formatDurationDe(durations.departure)}` : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "Ist-Zeiten unvollständig – Beginn und Ende der Arbeit angeben.";

  return (
    <p
      className={`text-xs text-slate-500 ${className ?? ""}`}
      aria-live="polite"
      data-testid="actual-times-summary"
    >
      {text}
    </p>
  );
};
